import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Handshake, Shield, Plus, Send, X, Lock, Search, Building2, Trash2, ShieldCheck, Link2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { CommanServices } from '../services/commanServices';
import socketService from '../services/socketService';

const fmt = (ts) => new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function PartnerPortalModal({ open, onClose }) {
  const [pages, setPages] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState({ name: '', org: '', accessCode: '' });
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const joinedRef = useRef(null);

  const loadPages = async () => {
    try {
      const res = await CommanServices.partnerPagesList();
      const list = res?.data || [];
      setPages(list);
      return list;
    } catch (e) { toast.error('Failed to load partner pages'); return []; }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      const list = await loadPages();
      if (list.length && !activeId) setActiveId(list[0].id);
    })();
  }, [open]);

  // Lock the underlying main column scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const els = [document.querySelector('main')].filter(Boolean);
    const prev = els.map((e) => [e, e.style.overflow]);
    els.forEach((e) => (e.style.overflow = 'hidden'));
    return () => prev.forEach(([e, v]) => (e.style.overflow = v));
  }, [open]);

  // Load messages + join realtime room when active page changes
  useEffect(() => {
    if (!open || !activeId) { setMessages([]); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await CommanServices.partnerMessagesList(activeId);
        if (!cancelled) setMessages(res?.data || []);
      } catch (e) { if (!cancelled) toast.error('Failed to load messages'); }
      finally { if (!cancelled) setLoading(false); }
    })();

    if (joinedRef.current && joinedRef.current !== activeId) {
      socketService.emit('leave_partner', { page_id: joinedRef.current });
    }
    socketService.emit('join_partner', { page_id: activeId });
    joinedRef.current = activeId;

    // Poll for new messages (reliable through /api ingress)
    const poll = setInterval(async () => {
      try {
        const res = await CommanServices.partnerMessagesList(activeId);
        if (!cancelled && Array.isArray(res?.data)) setMessages(res.data);
      } catch (e) { /* noop */ }
    }, 2500);

    return () => { cancelled = true; clearInterval(poll); };
  }, [open, activeId]);

  // Realtime listener for incoming partner messages
  useEffect(() => {
    if (!open) return;
    const handler = (payload) => {
      if (!payload || payload.page_id !== activeId || !payload.message) return;
      setMessages((prev) => (prev.some((m) => m.id === payload.message.id) ? prev : [...prev, payload.message]));
    };
    const unsub = socketService.on('partner_message', handler);
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [open, activeId]);

  // Leave room on close
  useEffect(() => {
    if (open) return;
    if (joinedRef.current) { socketService.emit('leave_partner', { page_id: joinedRef.current }); joinedRef.current = null; }
  }, [open]);

  const active = useMemo(() => pages.find((p) => p.id === activeId) || null, [pages, activeId]);
  const filtered = useMemo(() => pages.filter((p) => (p.name + (p.org || '')).toLowerCase().includes(search.toLowerCase())), [pages, search]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages.length, activeId]);

  if (!open) return null;

  const createPage = async () => {
    if (!draft.name.trim()) return;
    try {
      const res = await CommanServices.partnerPageCreate({ name: draft.name.trim(), org: draft.org.trim(), access_code: draft.accessCode.trim() });
      if (res?.status && res.data) {
        await loadPages();
        setActiveId(res.data.id);
        setCreating(false);
        setDraft({ name: '', org: '', accessCode: '' });
        toast.success('Secure partner page created');
      } else { toast.error(res?.message || 'Could not create page'); }
    } catch (e) { toast.error('Could not create page'); }
  };

  const sendMsg = async () => {
    if (!msg.trim() || !active) return;
    const text = msg.trim();
    setMsg('');
    try {
      const res = await CommanServices.partnerMessageSend(active.id, { text });
      if (res?.status && res.data) {
        setMessages((prev) => (prev.some((m) => m.id === res.data.id) ? prev : [...prev, res.data]));
      } else { toast.error(res?.message || 'Message failed'); }
    } catch (e) { toast.error('Message failed'); }
  };

  const removePage = async (id) => {
    if (!window.confirm('Delete this secure partner page?')) return;
    try {
      await CommanServices.partnerPageDelete(id);
      const list = await loadPages();
      if (activeId === id) setActiveId(list[0]?.id || null);
      toast.success('Partner page deleted');
    } catch (e) { toast.error('Could not delete page'); }
  };

  const copyLink = () => {
    if (!active) return;
    const link = `${window.location.origin}/partner/${active.slug}`;
    const text = `Secure partner channel: ${active.name}\nLink: ${link}\nAccess code: ${active.accessCode}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Secure link + access code copied');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error('Copy failed'));
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" data-testid="partner-portal-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[82vh] flex overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Sidebar */}
        <div className="w-[300px] bg-slate-900 text-white flex flex-col flex-shrink-0">
          <div className="px-4 py-4 border-b border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <Handshake className="h-5 w-5 text-indigo-300" />
              <h3 className="font-bold text-[15px]">Strategic Partners</h3>
            </div>
            <p className="text-[11px] text-slate-400">Secure collaboration pages</p>
          </div>
          <div className="p-3">
            <button data-testid="create-partner-page-btn" onClick={() => { setCreating(true); setActiveId(null); }}
              className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold py-2.5 transition-colors">
              <Plus className="h-3.5 w-3.5" /> New Secure Page
            </button>
            <div className="relative mt-3">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search partners…"
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-2 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-400" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1" data-testid="partner-page-list">
            {filtered.map((p) => (
              <button key={p.id} data-testid={`partner-page-${p.slug}`} onClick={() => { setActiveId(p.id); setCreating(false); }}
                className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors group ${activeId === p.id ? 'bg-indigo-600' : 'hover:bg-white/5'}`}>
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 flex-shrink-0"><Building2 className="h-4 w-4 text-indigo-200" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold truncate">{p.name}</p>
                    <p className="text-[10px] text-slate-300 truncate">{p.org || 'External partner'}</p>
                  </div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && <p className="text-[11px] text-slate-500 text-center py-6">No partner pages.</p>}
          </div>
          <div className="px-4 py-3 border-t border-white/10 flex items-center gap-1.5 text-[10px] text-emerald-300">
            <ShieldCheck className="h-3.5 w-3.5" /> End-to-end secured channel
          </div>
        </div>

        {/* Main panel */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-900">{creating ? 'Create secure partner page' : (active?.name || 'Select a partner')}</h3>
            <button onClick={onClose} data-testid="partner-portal-close" className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
          </div>

          {creating ? (
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-md mx-auto space-y-4">
                <div className="flex items-center gap-2 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2.5 text-[12px] text-indigo-700">
                  <Shield className="h-4 w-4" /> Create an isolated, access-controlled page to coordinate with a strategic partner.
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Page name *</label>
                  <input autoFocus value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Civil Defense Liaison" data-testid="partner-name"
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Partner organization</label>
                  <input value={draft.org} onChange={(e) => setDraft({ ...draft, org: e.target.value })} placeholder="e.g. Saudi Red Crescent Authority" data-testid="partner-org"
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1"><Lock className="h-3 w-3" /> Access code (optional)</label>
                  <input value={draft.accessCode} onChange={(e) => setDraft({ ...draft, accessCode: e.target.value })} placeholder="Auto-generated if left blank" data-testid="partner-code"
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 mt-1" />
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={() => setCreating(false)} className="flex-1 border border-gray-300 rounded-md py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button onClick={createPage} disabled={!draft.name.trim()} data-testid="partner-create-submit"
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md py-2.5 text-sm font-bold">Create page</button>
                </div>
              </div>
            </div>
          ) : active ? (
            <>
              <div className="px-5 py-2.5 bg-slate-50 border-b border-gray-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[12px] text-gray-600 min-w-0">
                  <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" /> <span className="truncate">{active.org || 'External partner'}</span>
                  <span className="inline-flex items-center gap-1 ml-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold flex-shrink-0"><Lock className="h-3 w-3" /> {active.accessCode}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={copyLink} data-testid="partner-copy-link"
                    className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 px-2.5 py-1.5 text-[11px] font-bold transition-colors">
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />} {copied ? 'Copied' : 'Copy secure link'}
                  </button>
                  <button onClick={() => removePage(active.id)} className="p-1.5 rounded-md text-gray-400 hover:text-rose-600 hover:bg-gray-100"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/40" data-testid="partner-messages">
                {!loading && messages.length === 0 && (
                  <div className="text-center text-gray-400 text-sm py-10"><Shield className="h-8 w-8 mx-auto mb-2 text-gray-300" />Secure channel established. Start the conversation.</div>
                )}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.fromPartner ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${m.fromPartner ? 'bg-white border border-gray-200 text-gray-800' : 'bg-indigo-600 text-white'}`}>
                      <p className="text-[10px] font-bold opacity-70 mb-0.5">{m.author}{m.fromPartner && m.org ? ` · ${m.org}` : ''}</p>
                      <p className="text-sm leading-relaxed">{m.text}</p>
                      <p className={`text-[10px] mt-1 ${m.fromPartner ? 'text-gray-400' : 'text-indigo-200'}`}>{fmt(m.ts)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-gray-100 flex items-center gap-2">
                <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendMsg()} placeholder="Type a secure message…" data-testid="partner-message-input"
                  className="flex-1 text-sm border border-gray-300 rounded-full px-4 py-2.5 focus:outline-none focus:border-indigo-400" />
                <button onClick={sendMsg} disabled={!msg.trim()} data-testid="partner-send"
                  className="flex items-center justify-center h-10 w-10 rounded-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white"><Send className="h-4 w-4" /></button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
              <div className="text-center"><Handshake className="h-10 w-10 mx-auto mb-2 text-gray-300" />Select a partner page or create a new secure page.</div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
